import { countyStateFromLocationLikeRow } from "../client/jurisdictionKeys";
import gazetteerData from "./usCountyGazetteer.json";

export type AssetCoordinateSource = "explicit" | "county" | "operator";

export type CountyGeographyPoint = { lat: number; lng: number };

export type CountyMapView = {
  center: [number, number];
  zoom: number;
};

type GazetteerRow = {
  fips: string;
  state: string;
  county: string;
  lat: number;
  lng: number;
};

const GAZETTEER = gazetteerData as GazetteerRow[];

const STATE_NAME_TO_USPS: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  "district of columbia": "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "american samoa": "AS",
  guam: "GU",
  "northern mariana islands": "MP",
  "puerto rico": "PR",
  "u s virgin islands": "VI",
  "us virgin islands": "VI",
  "virgin islands": "VI",
};

const byFips = new Map<string, GazetteerRow>();
const byStateCounty = new Map<string, GazetteerRow>();

function normalizeToken(value: string): string {
  return value
    .toLowerCase()
    .replace(/[.']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function stripAdminSuffix(normalized: string): string {
  return normalized
    .replace(/\bcounty$/i, "")
    .replace(/\bparish$/i, "")
    .replace(/\bborough$/i, "")
    .replace(/\bcensus area$/i, "")
    .replace(/\bmunicipality$/i, "")
    .replace(/\bcity and borough$/i, "")
    .trim();
}

function normalizeCountyName(county: string): string {
  return stripAdminSuffix(normalizeToken(county));
}

function padFips(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return digits.padStart(5, "0").slice(-5);
}

for (const row of GAZETTEER) {
  byFips.set(padFips(row.fips), row);
  const key = `${row.state}|${normalizeCountyName(row.county)}`;
  if (!byStateCounty.has(key)) byStateCounty.set(key, row);
}

/** FIPS from an `assetLocations` / jurisdiction row when Zoho publishes it (`fips` / `FIPS`). */
export function fipsFromLocationLikeRow(row: Record<string, unknown>): string | null {
  const raw = row.fips ?? row.Fips ?? row.FIPS;
  if (typeof raw === "string" && raw.trim()) return padFips(raw.trim());
  if (typeof raw === "number" && Number.isFinite(raw)) return padFips(String(Math.trunc(raw)));
  return null;
}

/** Normalize full state name or USPS code to two-letter USPS. */
export function stateToUsps(state: string | null | undefined): string | null {
  if (!state?.trim()) return null;
  const trimmed = state.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  const mapped = STATE_NAME_TO_USPS[normalizeToken(trimmed)];
  return mapped ?? null;
}

/** Resolve one county row to Census internal point (lat/lng). */
export function lookupCountyGeography(params: {
  fips?: string | null;
  county?: string | null;
  state?: string | null;
}): CountyGeographyPoint | null {
  if (params.fips) {
    const byCode = byFips.get(padFips(params.fips));
    if (byCode) return { lat: byCode.lat, lng: byCode.lng };
  }
  const usps = stateToUsps(params.state);
  const county = params.county?.trim();
  if (!usps || !county) return null;
  const row = byStateCounty.get(`${usps}|${normalizeCountyName(county)}`);
  if (!row) return null;
  return { lat: row.lat, lng: row.lng };
}

/** Bounding-box center + heuristic zoom for one or more county internal points. */
export function combinedCountyMapView(points: readonly CountyGeographyPoint[]): CountyMapView | null {
  if (!points.length) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }

  const center: [number, number] = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  const latSpan = Math.max(maxLat - minLat, 0.02);
  const lngSpan = Math.max(maxLng - minLng, 0.02);
  const span = Math.max(latSpan, lngSpan);

  let zoom = 7;
  if (span < 0.08) zoom = 10;
  else if (span < 0.2) zoom = 9;
  else if (span < 0.45) zoom = 8;
  else if (span < 1) zoom = 7;
  else if (span < 2.5) zoom = 6;
  else if (span < 6) zoom = 5;
  else zoom = 4;

  if (points.length === 1) zoom = Math.min(zoom, 9);

  return { center, zoom: Math.min(14, Math.max(4, zoom)) };
}

function assetLocationRows(asset: Record<string, unknown>): Record<string, unknown>[] {
  const locs =
    asset.assetLocations ??
    asset.AssetLocations ??
    asset.counties ??
    asset.Counties;
  if (!Array.isArray(locs)) return [];
  return locs.filter((loc): loc is Record<string, unknown> => !!loc && typeof loc === "object");
}

/** Internal Census points for each county on an asset; empty when none resolve. */
export function resolveAssetCountyPoints(asset: Record<string, unknown>): CountyGeographyPoint[] {
  const points: CountyGeographyPoint[] = [];
  const seen = new Set<string>();

  for (const row of assetLocationRows(asset)) {
    const fips = fipsFromLocationLikeRow(row);
    const cs = countyStateFromLocationLikeRow(row);
    const geo = lookupCountyGeography({
      fips,
      county: cs?.county,
      state: cs?.state,
    });
    if (!geo) continue;

    const dedupeKey = fips ?? (cs ? `${cs.county}|${cs.state}` : "");
    if (!dedupeKey || seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    points.push(geo);
  }

  return points;
}

/** County-derived map view from an asset's location rows; null when no counties resolve. */
export function resolveAssetCountyMapView(asset: Record<string, unknown>): CountyMapView | null {
  return combinedCountyMapView(resolveAssetCountyPoints(asset));
}

export const US_COUNTY_GAZETTEER_ROW_COUNT = GAZETTEER.length;
